import { apiClient, type ApiResponse } from '@/api/client';
import type { MockInspection, MockInspectionResult, MockNCR, MockCAPA } from '@/mocks/qms';

const BASE = '/api/v1/manufacturing/qc';

export interface InspectionFilterParams {
  type?: 'INCOMING' | 'IN_PROCESS' | 'FINAL';
  status?: string;
  search?: string;
  sourceFilter?: string;
  /** A production order's inspections, its work orders' stage QC included */
  productionOrderId?: string;
  dateFrom?: string;
  dateTo?: string;
  warehouseId?: string;
}

export interface NCRFilterParams {
  type?: string;
  severity?: string;
  status?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  warehouseId?: string;
}

export const qcService = {
  // ── Inspections ─────────────────────────────────────────────────────────────
  async getInspections(params?: InspectionFilterParams): Promise<MockInspection[]> {
    const { data } = await apiClient.get<ApiResponse<MockInspection[]>>(`${BASE}/inspections`, {
      params,
    });
    return data.data || [];
  },

  async getInspectionById(
    id: string
  ): Promise<{ inspection: MockInspection; results: MockInspectionResult[] }> {
    const { data } = await apiClient.get<
      ApiResponse<{ inspection: MockInspection; results: MockInspectionResult[] }>
    >(`${BASE}/inspections/${id}`);
    return data.data;
  },

  async createInspection(payload: Partial<MockInspection>): Promise<MockInspection> {
    const { data } = await apiClient.post<ApiResponse<MockInspection>>(
      `${BASE}/inspections`,
      payload
    );
    return data.data;
  },

  async submitInspectionResults(
    id: string,
    payload: {
      results: Partial<MockInspectionResult>[];
      overallStatus: 'PASSED' | 'FAILED';
    }
  ): Promise<void> {
    await apiClient.put(`${BASE}/inspections/${id}/submit`, payload);
  },

  // ── NCRs ───────────────────────────────────────────────────────────────────
  async getNCRs(params?: NCRFilterParams): Promise<MockNCR[]> {
    const { data } = await apiClient.get<ApiResponse<MockNCR[]>>(`${BASE}/ncr`, { params });
    return data.data || [];
  },

  async createNCR(payload: Partial<MockNCR>): Promise<MockNCR> {
    const { data } = await apiClient.post<ApiResponse<MockNCR>>(`${BASE}/ncr`, payload);
    return data.data;
  },

  async updateNCRStatus(
    id: string,
    payload: { status?: string; dispositionAction?: string; reviewedBy?: string; closedDate?: string }
  ): Promise<MockNCR> {
    const { data } = await apiClient.put<ApiResponse<MockNCR>>(`${BASE}/ncr/${id}/status`, payload);
    return data.data;
  },

  // ── CAPAs ───────────────────────────────────────────────────────────────────
  async getCAPAs(): Promise<MockCAPA[]> {
    const { data } = await apiClient.get<ApiResponse<MockCAPA[]>>(`${BASE}/capa`);
    return data.data || [];
  },

  async createCAPA(payload: Partial<MockCAPA>): Promise<MockCAPA> {
    const { data } = await apiClient.post<ApiResponse<MockCAPA>>(`${BASE}/capa`, payload);
    return data.data;
  },

  async updateCAPAStatus(id: string, status: string): Promise<MockCAPA> {
    const { data } = await apiClient.put<ApiResponse<MockCAPA>>(`${BASE}/capa/${id}/status`, {
      status,
    });
    return data.data;
  },

  async verifyCAPA(
    id: string,
    payload: { verifiedBy?: string; effectiveness?: string; notes?: string }
  ): Promise<MockCAPA> {
    const { data } = await apiClient.put<ApiResponse<MockCAPA>>(`${BASE}/capa/${id}/verify`, payload);
    return data.data;
  },
};
